import { useCallback, useMemo } from 'react';
import { Setting } from '@weave/schema-gen-ts/dist/schemas/search/v1/service.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { settingsRouteLookup, useSettingsPathLabelMap } from '@frontend/settings-routing';
import { useWeaveAssistantSettingsFeatureFlag } from '@frontend/weave-assistant-hooks';

const normalizeSettingsPath = (path: string) => {
  const pathWithoutSettingsPrefix = path.startsWith('/settings') ? path.replace('/settings', '') : path;
  return pathWithoutSettingsPrefix.endsWith('/') ? pathWithoutSettingsPrefix.slice(0, -1) : pathWithoutSettingsPrefix;
};

// TODO: remove this old hook when semantic-search FF is fully implemented
/**
 * This hook filters settings using the fuse settings that we set up in the instantiated RoutePathLookup class.
 * @param search The search term to filter settings by.
 * @returns An array of filtered settings based on the search word and user permission and feature flags, each containing a labelPath and path.
 * @deprecated This hook is deprecated and will be removed in the future. Use `useFilterSettings` instead.
 */
export const useFilterSettingsOld = (search: string): { labelPath: string; path: string }[] => {
  const { selectedLocationIds } = useAppScopeStore();
  const hasPhonefullAccess = hasSchemaACL(selectedLocationIds[0], Permission.PHONE_FULL_ACCESS);

  const filteredSettings = useMemo(() => {
    const settings = settingsRouteLookup.searchKeyword(search).map((item) => item.item);
    return settings.filter((setting) => {
      if (setting.path === '/settings/phone/call-routes/') {
        return hasPhonefullAccess;
      }
      if (setting.path === '/settings/phone/call-recording/') {
        return hasPhonefullAccess;
      }
      return true; // Allow all other settings
    });
  }, [hasPhonefullAccess, search]);

  return filteredSettings;
};

/**
 * This hook filters settings based on the user's permissions and feature flags.
 * @param settings An array of Setting objects to filter.
 * @returns An array of filtered settings based on the user's permissions and feature flags.
 * */
export const useFilterSettings = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const hasPhonefullAccess = hasSchemaACL(selectedLocationIds[0], Permission.PHONE_FULL_ACCESS);
  const hasWeaveAssistantSettings = useWeaveAssistantSettingsFeatureFlag();
  const settingsPathLabelMap = useSettingsPathLabelMap();

  const filterSetting = useCallback(
    (settings: Setting[], searchTerm?: string) => {
      const filteredSettings = settings.filter((setting) => {
        // Filter out Office Hours since it's being removed
        if (setting.path === '/settings/phone/office-hours/') {
          return false;
        }
        // Check for Weave Assistant settings feature flag
        if (setting.path.includes('/settings/weave-assistant/')) {
          return hasWeaveAssistantSettings;
        }
        if (setting.path === '/settings/phone/call-routes/') {
          return hasPhonefullAccess;
        }
        if (setting.path === '/settings/phone/call-recording/') {
          return hasPhonefullAccess;
        }
        return true; // Allow all other settings
      });

      const normalizedSearchTerm = searchTerm?.trim().toLowerCase();
      if (!normalizedSearchTerm) {
        return filteredSettings;
      }

      return filteredSettings.map((setting) => {
        const pathConfig =
          settingsPathLabelMap[normalizeSettingsPath(setting.path) as keyof typeof settingsPathLabelMap];
        const matchedAlias = pathConfig?.aliases?.find((alias) => normalizedSearchTerm.includes(alias.toLowerCase()));

        if (!matchedAlias) {
          return setting;
        }

        return {
          ...setting,
          name: `${setting.name} (${matchedAlias})`,
        };
      });
    },
    [hasPhonefullAccess, hasWeaveAssistantSettings, settingsPathLabelMap]
  );

  return { filterSetting };
};
